import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { ChevronRight, Clock, CalendarDays, ArrowRight, ChevronDown, List, Lightbulb, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import SEO from '../../../components/SEO';
import StructuredData from '../../../components/StructuredData';
import LandingNavBar from '../../../components/LandingNavBar';
import LandingFooter from '../../../components/LandingFooter';
import beds24Articles, { CATEGORY_META, findBeds24Article, formatBeds24Date } from './beds24Content';

const SITE = 'https://staymii.com';

/* ---------------- inline markdown (bold, code, links) ---------------- */
const inline = (text, keyPrefix = 'i') => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-${i}`} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={`${keyPrefix}-${i}`} className="bg-slate-100 text-[#1D4ED8] px-1.5 py-0.5 rounded text-[0.85em] font-mono">{part.slice(1, -1)}</code>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, url] = link;
      const external = /^https?:\/\//.test(url);
      return external
        ? <a key={`${keyPrefix}-${i}`} href={url} target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:text-[#1D4ED8] underline underline-offset-2 font-medium">{label}</a>
        : <Link key={`${keyPrefix}-${i}`} to={url} className="text-[#2563EB] hover:text-[#1D4ED8] underline underline-offset-2 font-medium">{label}</Link>;
    }
    return <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
};

const CALLOUT_STYLES = {
  tip:  { icon: Lightbulb,   cls: 'border-emerald-200 bg-emerald-50', ic: 'text-emerald-600', label: 'text-emerald-800' },
  warn: { icon: AlertTriangle, cls: 'border-amber-200 bg-amber-50',   ic: 'text-amber-600',   label: 'text-amber-800' },
  info: { icon: Info,        cls: 'border-blue-200 bg-blue-50',       ic: 'text-blue-600',    label: 'text-blue-800' },
};

/* ---------------- block renderer ---------------- */
const renderBody = (body) => {
  const lines = body.split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];

    // blank
    if (line.trim() === '') { i++; continue; }

    // code fence
    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const code = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) { code.push(lines[i]); i++; }
      i++; // closing fence
      blocks.push(
        <pre key={key++} className="my-5 overflow-x-auto rounded-xl bg-[#0B1121] p-4 text-sm leading-relaxed">
          {lang && <div className="mb-2 text-[11px] uppercase tracking-widest text-white/40">{lang}</div>}
          <code className="font-mono text-slate-200 whitespace-pre">{code.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // table
    if (line.trim().startsWith('|')) {
      const tbl = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) { tbl.push(lines[i].trim()); i++; }
      const rows = tbl.filter(r => !/^\|[\s:|-]+\|$/.test(r)).map(r => r.slice(1, -1).split('|').map(c => c.trim()));
      const [head, ...bodyRows] = rows;
      blocks.push(
        <div key={key++} className="my-6 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>{head.map((h, hi) => <th key={hi} className="px-4 py-3 text-left font-semibold text-slate-700 border-b border-slate-200">{inline(h, `th${key}-${hi}`)}</th>)}</tr>
            </thead>
            <tbody>
              {bodyRows.map((r, ri) => (
                <tr key={ri} className={ri % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                  {r.map((c, ci) => <td key={ci} className="px-4 py-3 text-slate-600 border-b border-slate-100 align-top">{inline(c, `td${key}-${ri}-${ci}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // callout  ::tip / ::warn / ::info  Text
    const co = line.match(/^::(tip|warn|info)\s+(.*)$/);
    if (co) {
      const [, type, text] = co;
      const s = CALLOUT_STYLES[type];
      const Icon = s.icon;
      blocks.push(
        <div key={key++} className={`my-5 flex gap-3 rounded-xl border p-4 ${s.cls}`}>
          <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${s.ic}`} />
          <p className={`text-sm leading-relaxed ${s.label}`}>{inline(text, `co${key}`)}</p>
        </div>
      );
      i++;
      continue;
    }

    // h3
    if (line.startsWith('### ')) {
      blocks.push(<h3 key={key++} className="text-xl font-bold text-slate-900 mt-8 mb-3">{inline(line.slice(4), `h3${key}`)}</h3>);
      i++;
      continue;
    }

    // ordered list
    if (/^\d+\.\s/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^\d+\.\s/, '')); i++; }
      blocks.push(<ol key={key++} className="my-4 ml-1 space-y-2 list-decimal list-inside text-slate-600 leading-relaxed">{items.map((it, ii) => <li key={ii}>{inline(it, `ol${key}-${ii}`)}</li>)}</ol>);
      continue;
    }

    // unordered list
    if (line.trim().startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) { items.push(lines[i].trim().slice(2)); i++; }
      blocks.push(
        <ul key={key++} className="my-4 space-y-2 text-slate-600 leading-relaxed">
          {items.map((it, ii) => (
            <li key={ii} className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 shrink-0 mt-1 text-[#2563EB]" /><span>{inline(it, `ul${key}-${ii}`)}</span></li>
          ))}
        </ul>
      );
      continue;
    }

    // paragraph
    blocks.push(<p key={key++} className="my-4 text-slate-600 leading-relaxed text-[15px]">{inline(line, `p${key}`)}</p>);
    i++;
  }
  return blocks;
};

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`faq-item-${idx}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-semibold text-slate-900 text-[15px]">{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{renderBody(a)}</div>}
    </div>
  );
};

const Beds24Article = () => {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, '');
  const article = findBeds24Article(path);

  useEffect(() => { window.scrollTo(0, 0); }, [path]);

  const related = useMemo(() => {
    if (!article) return [];
    return (article.related || []).map(s => beds24Articles.find(a => a.slug === s)).filter(Boolean);
  }, [article]);

  if (!article) return <Navigate to="/beds24" replace />;

  const cat = CATEGORY_META[article.category] || CATEGORY_META.Core;
  const url = `/beds24/${article.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE + '/' },
      { '@type': 'ListItem', position: 2, name: 'Beds24 Knowledge Centre', item: SITE + '/beds24' },
      { '@type': 'ListItem', position: 3, name: article.title, item: SITE + url },
    ],
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Organization', name: 'StayMii' },
    publisher: { '@type': 'Organization', name: 'StayMii', logo: { '@type': 'ImageObject', url: SITE + '/staymii-logo-full.png' } },
    dateModified: article.updated,
    datePublished: article.published || article.updated,
    mainEntityOfPage: SITE + url,
  };
  const faqSchema = article.faqs && article.faqs.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a.replace(/\n/g, ' ').replace(/[*`>#-]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').trim() },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-white" data-testid="beds24-article-page">
      <LandingNavBar />
      <SEO title={article.seoTitle || article.title} description={article.description} keywords={article.keywords} canonicalUrl={url} />
      <StructuredData id="beds24-breadcrumb" schema={breadcrumbSchema} />
      <StructuredData id="beds24-article" schema={articleSchema} />
      {faqSchema && <StructuredData id="beds24-faq" schema={faqSchema} />}

      {/* Hero */}
      <section className="pt-28 pb-12 bg-[#0B1121]" data-testid="beds24-article-hero">
        <div className="max-w-6xl mx-auto px-4">
          {/* breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-sm text-white/40 mb-6 flex-wrap" data-testid="beds24-breadcrumbs">
            <Link to="/" className="hover:text-white/70">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/beds24" className="hover:text-white/70">Beds24</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-white/60 truncate max-w-[60vw]">{article.title}</span>
          </nav>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${cat.badge}`}>{article.category}</span>
            <span className="text-white/40 text-sm flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.readTime}</span>
            <span className="text-white/30 text-sm flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Updated {formatBeds24Date(article.updated)}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 max-w-4xl">{article.title}</h1>
          <p className="text-white/50 text-base lg:text-lg max-w-3xl">{article.description}</p>
        </div>
      </section>

      {/* Body + sticky TOC */}
      <div className="max-w-6xl mx-auto px-4 py-10 lg:py-14 grid lg:grid-cols-[1fr_260px] gap-10">
        <article data-testid="beds24-article-content">
          {article.sections.map((s, si) => (
            <section key={si} id={`section-${si}`} className="mb-10 scroll-mt-28">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">{s.heading}</h2>
              {renderBody(s.body)}
            </section>
          ))}

          {/* FAQ */}
          {article.faqs && article.faqs.length > 0 && (
            <section id="faq" className="mb-10 scroll-mt-28" data-testid="beds24-faq">
              <h2 className="text-2xl font-bold text-slate-900 mb-5 pb-2 border-b border-slate-100">Frequently Asked Questions</h2>
              <div className="space-y-3">
                {article.faqs.map((f, fi) => <FaqItem key={fi} idx={fi} q={f.q} a={f.a} />)}
              </div>
            </section>
          )}

          {/* inline CTA */}
          <div className="rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] p-8 text-center" data-testid="beds24-inline-cta">
            <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">Run Beds24 in Malaysia? Automate your e-Invoicing.</h3>
            <p className="text-white/80 text-sm mb-6 max-w-xl mx-auto">StayMii connects Beds24 to LHDN MyInvois so every booking becomes a compliant e-invoice — automatically. Setup takes under 15 minutes.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" className="px-7 py-3 bg-white text-[#1D4ED8] rounded-xl font-semibold hover:bg-white/90 transition-colors" data-testid="beds24-cta-demo">Book a StayMii Demo</Link>
              <Link to="/integrations/beds24-e-invoice" className="px-7 py-3 bg-white/10 text-white border border-white/20 rounded-xl font-semibold hover:bg-white/20 transition-colors" data-testid="beds24-cta-learn">See the Integration</Link>
            </div>
          </div>
        </article>

        {/* sticky TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <p className="flex items-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3"><List className="h-4 w-4" /> On this page</p>
            <nav className="space-y-1 border-l border-slate-200">
              {article.sections.map((s, si) => (
                <a key={si} href={`#section-${si}`} className="block pl-4 -ml-px border-l-2 border-transparent hover:border-[#2563EB] py-1.5 text-sm text-slate-500 hover:text-[#2563EB] transition-colors" data-testid={`beds24-toc-${si}`}>{s.heading}</a>
              ))}
              {article.faqs && article.faqs.length > 0 && (
                <a href="#faq" className="block pl-4 -ml-px border-l-2 border-transparent hover:border-[#2563EB] py-1.5 text-sm text-slate-500 hover:text-[#2563EB] transition-colors" data-testid="beds24-toc-faq">FAQ</a>
              )}
            </nav>
          </div>
        </aside>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-12 px-4 bg-slate-50 border-t border-slate-100" data-testid="beds24-related">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Related in the Beds24 Knowledge Centre</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => {
                const rc = CATEGORY_META[r.category] || CATEGORY_META.Core;
                return (
                  <Link key={r.slug} to={`/beds24/${r.slug}`} className="block bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-md transition-all group" data-testid={`beds24-related-${r.slug}`}>
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium border mb-3 ${rc.badge}`}>{r.category}</span>
                    <h3 className="font-semibold text-slate-900 group-hover:text-[#2563EB] transition-colors mb-2 leading-snug">{r.title}</h3>
                    <p className="text-slate-500 text-sm line-clamp-2 mb-3">{r.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-[#2563EB] text-sm font-medium group-hover:gap-2.5 transition-all">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                  </Link>
                );
              })}
            </div>
            <div className="mt-8 text-center">
              <Link to="/beds24" className="inline-flex items-center gap-2 text-[#2563EB] font-medium hover:gap-3 transition-all" data-testid="beds24-back-hub">Browse all Beds24 guides <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      )}

      <LandingFooter />
    </div>
  );
};

export default Beds24Article;
